import React, { useEffect, useRef, useState } from "react";
import { copyText } from "@/lib/clipboard";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Star, Copy, Check, Loader2, Save, Search, X, CalendarClock } from "lucide-react";
import { http } from "@/lib/api";
import { useOptions } from "@/hooks/useOptions";
import { userOptions } from "@/lib/users";
import { SearchSelect } from "@/components/kit/SearchSelect";
import { useResourceMutations } from "@/hooks/useResource";
import { TextField, TextareaField, SelectField, Field } from "@/components/kit/fields";
import { CountrySelect } from "@/components/kit/CountrySelect";
import { CurrencySelect } from "@/components/kit/CurrencySelect";
import { StatusBadge } from "@/components/kit/StatusBadge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtMoney, flagEmoji, geoName, cleanOfferName, fmtDate, fmtDateTime } from "@/lib/format";
import { GeoOverridesDialog } from "@/components/users/GeoOverridesDialog";
import { cn, uid } from "@/lib/utils";
import { usePlatformTrackingDomains } from "@/lib/domains";
import { AssetUpload } from "@/components/branding/AssetUpload";
import { Switch } from "@/components/ui/switch";
import { OfferFilters } from "@/components/offers/OfferFilters";
import { assetUrl } from "@/lib/branding";
import { toast } from "sonner";

const TABS = ["General", "Landing", "Users", "Streams", "Filters", "Changes"];
const MACROS = ["{clickid}", "{sub1}", "{sub2}", "{sub3}", "{sub4}", "{sub5}", "{country}", "{device}", "{browser}", "{campaign}", "{user_id}", "{stream_id}", "{offer_id}"];

function Panel({ title, testId, className, children }) {
  return (
    <div className={cn("rounded-xl border border-white/[0.06] bg-transparent p-5", className)} data-testid={testId}>
      <h4 className="mb-4 text-sm font-medium text-white">{title}</h4>
      {children}
    </div>
  );
}

const empty = {
  name: "", advertiser_id: "", product_id: "", geo: "", geos: [], geo_rates: {}, vertical: "", payment_model: "CPA",
  advertiser_payout: "", affiliate_payout: "", currency: "USD", hold: "", cap: 0, dynamic_revenue: false,
  min_dep: "", min_dep_currency: "",
  rs_affiliate_percent: "", rs_advertiser_revenue: "", rs_dynamic_revenue: false, rs_adv_percent: "",
  privacy: "public", status: "active", description: "", private_description: "", source_id: "",
  tracking_domain: "", tracking_domains: [],
  tracking: { tracking_url: "", redirect_url: "", preview_url: "", postback_url: "", parameters: "" },
  landings: [], domains: [], creatives: [], filters: [],
  landing_mode: "classic", geo_landings: [],
};

function SectionTitle({ children, center }) {
  return <h4 className={cn("mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground/70", center && "text-center")}>{children}</h4>;
}

export function OfferEditor({ offerId, initial, onSaved, readOnly, onDirtyChange }) {
  const opts = useOptions();
  // Users who can own a stream / be granted Private-landing access: buyers, managers and
  // tenant admins (platform-wide user directory).
  const streamOwnerOptions = React.useMemo(() => userOptions(opts.users), [opts.users]);
  const { create, update } = useResourceMutations("offers");
  const [tab, setTab] = useState("General");
  const [form, setForm] = useState({ ...empty, ...(initial || {}) });
  const [cascade, setCascade] = useState(null); // { saved, landingChanged, domainChanged, prev*, new*, landingChoice, domainChoice }
  const baseline = useRef(JSON.stringify({ ...empty, ...(initial || {}) }));
  const isEdit = !!offerId;

  const { data: loaded, isLoading } = useQuery({
    queryKey: ["offer", offerId],
    queryFn: async () => (await http.get(`/offers/${offerId}`)).data,
    enabled: isEdit,
  });

  useEffect(() => {
    if (loaded) {
      const b = { ...empty, ...loaded, tracking: { ...empty.tracking, ...(loaded.tracking || {}) } };
      b.geos = Array.isArray(loaded.geos) && loaded.geos.length
        ? loaded.geos
        : String(loaded.geo || "").split(",").map((x) => x.trim().toUpperCase()).filter(Boolean);
      b.geo_rates = loaded.geo_rates || {};
      setForm(b);
      baseline.current = JSON.stringify(b);
    }
  }, [loaded]);

  useEffect(() => { onDirtyChange?.(JSON.stringify(form) !== baseline.current); }, [form]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTrack = (k, v) => setForm((f) => ({ ...f, tracking: { ...f.tracking, [k]: v } }));
  const isRevShare = form.payment_model === "RevShare";
  const isHybrid = form.payment_model === "Hybrid";
  const profit = (Number(form.advertiser_payout) || 0) - (Number(form.affiliate_payout) || 0);

  // Tracking domains = current Primary Platform Domain (+ dedicated tracking subdomain) + the
  // tenant's own verified tracking domains. Single source of truth — no legacy field / raw IP.
  const td = usePlatformTrackingDomains();
  const defaultTd = td.defaultDomain;
  const tdOptions = td.options;
  const savedSel = (form.tracking_domains && form.tracking_domains.length) ? form.tracking_domains : [form.tracking_domain];
  const selectedTds = savedSel.filter((d) => tdOptions.includes(d));
  if (selectedTds.length === 0 && defaultTd) selectedTds.push(defaultTd);
  const defaultTdValue = tdOptions.includes(form.tracking_domain) ? form.tracking_domain : defaultTd;
  const previewLink = td.linkFor(defaultTdValue, "pLyp26rf");

  const toggleTd = (d) => setForm((f) => {
    const cur = (f.tracking_domains && f.tracking_domains.length) ? f.tracking_domains : [f.tracking_domain || defaultTd];
    let next = cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d];
    if (next.length === 0) next = [defaultTd];
    let def = f.tracking_domain || defaultTd;
    if (!next.includes(def)) def = next[0];
    return { ...f, tracking_domains: next, tracking_domain: def };
  });
  const setDefaultTd = (d) => setForm((f) => {
    const cur = (f.tracking_domains && f.tracking_domains.length) ? f.tracking_domains : [f.tracking_domain || defaultTd];
    const next = cur.includes(d) ? cur : [...cur, d];
    return { ...f, tracking_domains: next, tracking_domain: d };
  });

  const buildBody = () => ({ ...form, hold: Number(form.hold) || 0, cap: Number(form.cap) || 0,
    source_id: form.source_id || null,
    geos: form.geos || [], geo: (form.geos || []).join(", "), geo_rates: form.geo_rates || {},
    min_dep: form.min_dep === "" || form.min_dep == null ? null : Number(form.min_dep),
    min_dep_currency: form.min_dep_currency || null,
    advertiser_payout: Number(form.advertiser_payout) || 0, affiliate_payout: Number(form.affiliate_payout) || 0,
    dynamic_revenue: !!form.dynamic_revenue,
    rs_affiliate_percent: Number(form.rs_affiliate_percent) || 0, rs_advertiser_revenue: Number(form.rs_advertiser_revenue) || 0,
    rs_dynamic_revenue: !!form.rs_dynamic_revenue, rs_adv_percent: Number(form.rs_adv_percent) || 0 });

  // Persist the offer (+ any cascade instructions) in ONE atomic request, then finalise.
  const commitSave = async (extra = {}) => {
    const body = { ...buildBody(), ...extra };
    const saved = isEdit ? await update.mutateAsync({ id: offerId, body }) : await create.mutateAsync(body);
    const c = saved?.cascade || {};
    if (c.landing_updated) toast.success(`Updated landing on ${c.landing_updated} stream${c.landing_updated > 1 ? "s" : ""}`);
    if (c.domain_frozen) toast.success("Existing streams kept on the previous domain");
    baseline.current = JSON.stringify(form);
    onDirtyChange?.(false);
    setCascade(null);
    onSaved?.(saved);
  };

  const save = async () => {
    if (!form.advertiser_id || !form.product_id) {
      toast.error("Advertiser and Product are required");
      setTab("General");
      return;
    }
    if ((form.landing_mode || "classic") === "multi_geo") {
      const fb = (form.geo_landings || []).find((l) => l.fallback && l.status !== "inactive" && l.url);
      if (!fb) { toast.error("Multi-GEO requires an active Fallback landing (All other GEOs)"); setTab("Landing"); return; }
    }
    const prevPrimary = loaded ? ((loaded.landings || []).find((l) => l.primary) || {}).name : null;
    const newPrimary = ((form.landings || []).find((l) => l.primary) || {}).name;
    const prevDomain = (loaded?.tracking_domain || "").toLowerCase();
    const newDomain = (form.tracking_domain || "").toLowerCase();
    // Did the previous default landing survive this edit? If it was DELETED we auto-reassign +
    // auto-cascade (no prompt) — the old landing no longer exists. If it was merely re-pointed
    // (still present, just not ★) we ask the user what to do with inheriting streams.
    const prevStillPresent = prevPrimary ? (form.landings || []).some((l) => l.name === prevPrimary) : true;
    const landingDeleted = isEdit && prevPrimary && !prevStillPresent && newPrimary && newPrimary !== prevPrimary;
    const landingChanged = isEdit && prevStillPresent && prevPrimary && newPrimary && prevPrimary !== newPrimary;
    const domainChanged = isEdit && prevDomain && newDomain && prevDomain !== newDomain;

    // A user decision is needed → open the cascade dialog BEFORE saving anything.
    if (landingChanged || domainChanged) {
      setCascade({ landingChanged, domainChanged, prevPrimary, newPrimary, prevDomain, newDomain,
        landingChoice: "replace", domainChoice: "migrate" });
      return; // nothing is saved until the dialog is confirmed
    }
    // No decision required — save directly. A deleted default landing cascades automatically.
    const extra = {};
    if (landingDeleted) extra.cascade_landing = { from_name: prevPrimary, to_name: newPrimary };
    await commitSave(extra);
  };

  const applyCascade = async () => {
    const c = cascade;
    const extra = {};
    if (c.landingChanged && c.landingChoice === "replace") extra.cascade_landing = { from_name: c.prevPrimary, to_name: c.newPrimary };
    if (c.domainChanged) extra.cascade_domain = { mode: c.domainChoice, old_default: c.prevDomain };
    try {
      await commitSave(extra);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save changes"); }
  };
  const skipCascade = () => setCascade(null); // Cancel → save nothing; form keeps the unsaved change.

  const addItem = (key, item) => set(key, [...(form[key] || []), { id: uid(), ...item }]);
  // Removing the ★ default landing auto-promotes the next Active landing to default so streams
  // never point at a deleted landing (the actual stream cascade happens on Save).
  const removeLanding = (id) => setForm((f) => {
    const removed = (f.landings || []).find((x) => x.id === id);
    const remaining = (f.landings || []).filter((x) => x.id !== id);
    if (removed?.primary && remaining.length) {
      const next = remaining.find((l) => l.status !== "inactive") || remaining[0];
      return { ...f, landings: remaining.map((l) => ({ ...l, primary: l.id === next.id })) };
    }
    return { ...f, landings: remaining };
  });
  const setPrimary = (key, id) => set(key, form[key].map((x) => ({ ...x, primary: x.id === id })));
  const updateItem = (key, id, patch) => set(key, form[key].map((x) => (x.id === id ? { ...x, ...patch } : x)));

  if (isEdit && isLoading) {
    return <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  return (
    <div data-testid="offer-editor">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div className="no-scrollbar flex gap-1 overflow-x-auto">
          {TABS.map((tName) => (
            <button key={tName} data-testid={`offer-tab-${tName.toLowerCase()}`} onClick={() => setTab(tName)}
              className={cn("relative whitespace-nowrap px-3 py-2 text-sm transition-colors",
                tab === tName ? "text-foreground" : "text-muted-foreground hover:text-foreground")}>
              {tName}
              {tab === tName && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-white" />}
            </button>
          ))}
        </div>
        {!readOnly && (
          <Button onClick={save} disabled={create.isPending || update.isPending} data-testid="offer-save"
            className="h-9 shrink-0 gap-1.5 bg-white text-black hover:bg-white/90">
            <Save className="h-4 w-4" /> {isEdit ? "Save changes" : "Create GEO offer"}
          </Button>
        )}
      </div>
      <div className="border-b border-white/[0.08]" />

      <div className="pt-5">
        {tab === "General" && (
          <div className="space-y-8">
            {/* Summary header — clean, premium, no developer labels */}
            <div className="flex flex-col gap-6 border-b border-white/10 pb-8" data-testid="offer-summary">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 space-y-2">
                  <div data-testid="offer-name" className="w-full max-w-xl truncate text-2xl font-semibold tracking-tight text-white">
                    {form.tenant_seq != null ? <span className="mr-1.5 text-muted-foreground">#{form.tenant_seq}</span> : null}
                    {form.product_name || cleanOfferName(form.name) || "Untitled GEO offer"}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground" data-testid="offer-summary-meta">
                    <span className="text-foreground">{form.advertiser_name || "—"}</span><span className="text-white/20">·</span>
                    <span>{form.product_name || "—"}</span>{form.vertical ? <><span className="text-white/20">·</span><span>{form.vertical}</span></> : null}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span data-testid="offer-summary-geo" className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-xs text-foreground">
                    {(form.geos || []).length > 1
                      ? (form.geos.slice(0, 4).map((g) => `${flagEmoji(g)} ${g}`).join(" ") + (form.geos.length > 4 ? ` +${form.geos.length - 4}` : ""))
                      : (form.geo ? `${flagEmoji(form.geo)} ${form.geo}` : "No GEO")}
                  </span>
                  <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs capitalize", form.status === "active" ? "bg-[#22C55E]/10 text-[#22C55E]" : "bg-white/[0.05] text-muted-foreground")}>
                    <span className={cn("h-1.5 w-1.5 rounded-full", form.status === "active" ? "bg-[#22C55E]" : "bg-zinc-500")} />{form.status}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6 md:grid-cols-5">
                {[["Payout", isRevShare ? `${Number(form.affiliate_payout) || 0}%` : fmtMoney(form.affiliate_payout)],
                  ["Revenue", form.dynamic_revenue ? "Dynamic" : fmtMoney(form.advertiser_payout)],
                  ["Profit", (isRevShare || form.dynamic_revenue) ? "—" : fmtMoney(profit), (isRevShare || form.dynamic_revenue) ? "" : (profit >= 0 ? "text-[#22C55E]" : "text-[#EF4444]")],
                  ...(isHybrid ? [["RevShare", `${Number(form.rs_affiliate_percent) || 0}%`]] : []),
                  ["Min Dep", form.min_dep ? `${form.min_dep} ${form.min_dep_currency || ""}`.trim() : "—"], ["Cap", form.cap ? form.cap : "∞"]].map(([l, v, tone]) => (
                  <div key={l} className="flex flex-col gap-1">
                    <span className="text-[11px] font-medium uppercase tracking-widest text-zinc-500">{l}</span>
                    <span className={cn("text-lg font-medium text-white", tone)}>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2 xl:grid-cols-12">
              <Panel title="Pricing" className="xl:col-span-5">
                <div className="grid grid-cols-2 gap-4">
                  {isRevShare ? (
                    <>
                      <GeoMultiPicker geos={form.geos} onChange={(v) => setForm((f) => ({ ...f, geos: v, geo: v.join(", ") }))} />
                      <TextField label="Advertiser Revenue (%)" type="number" value={form.rs_adv_percent} onChange={(v) => set("rs_adv_percent", v)} testId="offer-rs-adv-percent"
                        hint="Share of the gross the advertiser pays us (50 = 50%). Empty = 100%" />
                    </>
                  ) : (
                    <>
                      <GeoMultiPicker geos={form.geos} onChange={(v) => setForm((f) => ({ ...f, geos: v, geo: v.join(", ") }))} />
                      <div className="col-span-2 grid grid-cols-2 gap-4">
                        <TextField label="Min Dep" type="number" value={form.min_dep} onChange={(v) => set("min_dep", v)} testId="offer-min-dep" />
                        <CurrencySelect label="Currency" value={form.min_dep_currency} onChange={(v) => set("min_dep_currency", v)} testId="offer-min-dep-currency" triggerClass="h-9" />
                      </div>
                    </>
                  )}
                  {isRevShare && (
                    <TextField label="Affiliate payout (%)" type="number" value={form.affiliate_payout} onChange={(v) => set("affiliate_payout", v)} testId="offer-aff-payout"
                      hint="Percent of the GROSS the web earns (30 = 30%)" />
                  )}
                  <TextField label={isRevShare ? "Advertiser revenue" : "Advertiser payout"} type="number" value={form.advertiser_payout} onChange={(v) => set("advertiser_payout", v)} testId="offer-adv-payout"
                    disabled={!!form.dynamic_revenue}
                    hint={form.dynamic_revenue ? "Locked — revenue comes from the incoming postback" : (isRevShare ? "Static revenue per RevShare conversion" : undefined)} />
                  {!isRevShare && (
                    <TextField label="Affiliate payout" type="number" value={form.affiliate_payout} onChange={(v) => set("affiliate_payout", v)} testId="offer-aff-payout" />
                  )}
                  {isRevShare && (
                    <div className="grid grid-cols-2 gap-3">
                      <TextField label="Min Dep" type="number" value={form.min_dep} onChange={(v) => set("min_dep", v)} testId="offer-min-dep-rs" />
                      <CurrencySelect label="Currency" value={form.min_dep_currency} onChange={(v) => set("min_dep_currency", v)} testId="offer-min-dep-currency-rs" triggerClass="h-9" />
                    </div>
                  )}
                  <TextField label="Cap (0 = unlimited)" type="number" value={form.cap} onChange={(v) => set("cap", v)} testId="offer-cap" />
                </div>
                <div className="mt-4 flex items-start justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2.5">
                  <div>
                    <p className="text-sm text-foreground">Use {"{revenue}"} from incoming postback</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {isRevShare
                        ? <>ON — each conversion's Revenue equals the incoming <code className="rounded bg-white/[0.06] px-1">revenue</code> parameter (missing/invalid → $0). OFF — the static Advertiser revenue is used. Payout = Revenue × % / 100 in both modes.</>
                        : <>Dynamic revenue: each conversion's Revenue equals the <code className="rounded bg-white/[0.06] px-1">revenue</code> parameter
                          of the incoming advertiser postback. Missing or invalid value → $0. Affiliate payout is not affected.</>}
                    </p>
                  </div>
                  <Switch checked={!!form.dynamic_revenue} onCheckedChange={(v) => set("dynamic_revenue", v)} data-testid="offer-dynamic-revenue" />
                </div>
                {(form.geos || []).length > 1 && (
                  <GeoRatesEditor geos={form.geos} rates={form.geo_rates} onChange={(v) => set("geo_rates", v)}
                    isRevShare={isRevShare} dynamicRevenue={!!form.dynamic_revenue} currency={form.currency} />
                )}
                {isHybrid && (
                  <div className="mt-4 rounded-lg border border-[#F59E0B]/25 bg-[#F59E0B]/[0.04] p-3" data-testid="offer-hybrid-rs-section">
                    <p className="text-sm font-medium text-foreground">RevShare section</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">Applied only to incoming <code className="rounded bg-white/[0.06] px-1">status=revshare</code> conversions. Fully independent from the CPA settings above (used for Sale).</p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <TextField label="Advertiser Revenue (%)" type="number" value={form.rs_adv_percent} onChange={(v) => set("rs_adv_percent", v)} testId="offer-rs-adv-percent"
                        hint="Share of the gross the advertiser pays us (50 = 50%). Empty = 100%" />
                      <TextField label="Affiliate payout (%)" type="number" value={form.rs_affiliate_percent} onChange={(v) => set("rs_affiliate_percent", v)} testId="offer-rs-percent"
                        hint="Percent of the GROSS the web earns (30 = 30%)" />
                      <TextField label="Advertiser revenue" type="number" value={form.rs_advertiser_revenue} onChange={(v) => set("rs_advertiser_revenue", v)} testId="offer-rs-revenue"
                        disabled={!!form.rs_dynamic_revenue}
                        hint={form.rs_dynamic_revenue ? "Locked — revenue comes from the incoming postback" : "Static revenue per RevShare conversion"} />
                    </div>
                    <div className="mt-3 flex items-start justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2.5">
                      <div>
                        <p className="text-sm text-foreground">Use {"{revenue}"} from incoming postback</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">ON — RevShare conversion Revenue equals the incoming <code className="rounded bg-white/[0.06] px-1">revenue</code> parameter (missing/invalid → $0). OFF — the static Advertiser revenue above is used. Payout = Revenue × % / 100.</p>
                      </div>
                      <Switch checked={!!form.rs_dynamic_revenue} onCheckedChange={(v) => set("rs_dynamic_revenue", v)} data-testid="offer-rs-dynamic-revenue" />
                    </div>
                  </div>
                )}
              </Panel>
              <Panel title="Settings" className="xl:col-span-4">
                <div className="grid grid-cols-2 gap-4">
                  <SelectField label="Payment model" value={form.payment_model} onChange={(v) => set("payment_model", v)} options={opts.payment_models} testId="offer-model" />
                  <SelectField label="Privacy" value={form.privacy} onChange={(v) => set("privacy", v)} options={["public", "private", "request"]} testId="offer-privacy" hint="Public = visible & instant streams · Private = hidden until granted · Request = visible, buyer must request access" />
                  <SelectField label="Status" value={form.status} onChange={(v) => set("status", v)} options={["active", "disabled"]} testId="offer-status" />
                  <SelectField label="Source" value={form.source_id || "all"} onChange={(v) => set("source_id", v === "all" ? "" : v)}
                    options={[{ value: "all", label: "All Sources" }, ...(opts.sources || []).map((s) => ({ value: s.id, label: s.name }))]}
                    testId="offer-source" hint="Traffic source of this GEO. All Sources = universal (default). Manage the list in Settings → Sources." />
                </div>
                <div className="mt-4 space-y-4">
                  <TextareaField label="Description" value={form.description} onChange={(v) => set("description", v)} testId="offer-description" rows={3} />
                  <TextareaField label="Private note (managers only)" value={form.private_description} onChange={(v) => set("private_description", v)} testId="offer-private-description" rows={2} />
                </div>
              </Panel>
              <Panel title="Tracking domains" testId="offer-tracking-block" className="md:col-span-2 xl:col-span-3">
              <p className="mb-3 text-xs text-muted-foreground/70">Select verified tracking domains. The ★ default is used by new streams; buyers can switch a stream to any selected domain.</p>
              <div className="space-y-2">
                {tdOptions.map((d) => {
                  const on = selectedTds.includes(d);
                  const isDef = defaultTdValue === d;
                  return (
                    <div key={d} className="flex items-center gap-3 rounded-lg border border-white/[0.08] bg-[#141417] px-3 py-2.5" data-testid={`offer-td-${d}`}>
                      <button type="button" onClick={() => toggleTd(d)} data-testid={`offer-td-toggle-${d}`}
                        className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors",
                          on ? "border-white bg-white text-black" : "border-white/20 text-transparent hover:border-white/40")}>
                        <Check className="h-3.5 w-3.5" />
                      </button>
                      <code className="flex-1 truncate font-mono text-xs text-foreground">{d}</code>
                      {on && (
                        <button type="button" onClick={() => setDefaultTd(d)} title="Set as default" data-testid={`offer-td-default-${d}`}
                          className={cn("shrink-0 transition-colors", isDef ? "text-[#EAB308]" : "text-muted-foreground hover:text-foreground")}>
                          <Star className="h-4 w-4" fill={isDef ? "#EAB308" : "none"} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center gap-2 rounded-lg border border-white/[0.1] bg-[#141417] px-3 py-2.5">
                <code className="flex-1 truncate font-mono text-xs text-muted-foreground" data-testid="offer-tracking-preview">{previewLink}</code>
                <button type="button" onClick={() => copyText(previewLink)} className="shrink-0 text-muted-foreground hover:text-foreground"><Copy className="h-3.5 w-3.5" /></button>
              </div>
              </Panel>
            </div>
          </div>
        )}

        {tab === "Landing" && (
          <div className="space-y-5">
            <div className="mx-auto w-full max-w-2xl space-y-2 rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 text-center" data-testid="landing-mode-block">
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">Landing mode</label>
              <div className="mx-auto flex max-w-sm gap-1.5" data-testid="landing-mode">
                {[{ value: "classic", label: "Classic" }, { value: "multi_geo", label: "Multi-GEO" }].map((o) => (
                  <button key={o.value} type="button" onClick={() => set("landing_mode", o.value)} data-testid={`landing-mode-${o.value}`}
                    className={cn("flex-1 rounded-lg border px-4 py-2 text-xs font-medium transition-colors",
                      (form.landing_mode || "classic") === o.value ? "border-[#22C55E]/50 bg-[#22C55E]/10 text-[#22C55E]" : "border-white/[0.1] text-muted-foreground hover:text-foreground")}>
                    {o.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {(form.landing_mode || "classic") === "multi_geo"
                  ? "Visitors are automatically routed to a landing based on their country. Traffic filters still run first. Switching modes keeps both configurations."
                  : "Streams can select a specific landing. Switching modes keeps both configurations."}
              </p>
              {(form.landing_mode || "classic") === "multi_geo" && !(form.geo_landings || []).some((l) => l.fallback && l.status !== "inactive" && l.url) && (
                <p className="rounded-lg border border-[#EAB308]/30 bg-[#EAB308]/[0.07] px-3 py-2 text-xs text-[#EAB308]" data-testid="multigeo-no-fallback-warning">
                  Multi-GEO needs an active 🌐 Fallback landing (All other GEOs) before the offer can be saved in this mode.
                </p>
              )}
            </div>
            {(form.landing_mode || "classic") === "classic" ? (
              <>
                <LandingsEditor items={form.landings} buyers={streamOwnerOptions}
                  onAdd={(item) => { addItem("landings", { ...item, primary: (form.landings || []).length === 0 }); toast.success("Landing added — remember to Save the offer"); }}
                  onRemove={(id) => removeLanding(id)} onPrimary={(id) => setPrimary("landings", id)}
                  onUpdate={(id, patch) => updateItem("landings", id, patch)} />
              </>
            ) : (
              <GeoLandingsEditor items={form.geo_landings}
                onAdd={(item) => { addItem("geo_landings", item); toast.success("Landing added — remember to Save the offer"); }}
                onRemove={(id) => set("geo_landings", (form.geo_landings || []).filter((x) => x.id !== id))}
                onUpdate={(id, patch) => updateItem("geo_landings", id, patch)} />
            )}
          </div>
        )}
        {tab === "Users" && (isEdit ? <OfferUsers offerId={offerId} draft={{
          payment_model: form.payment_model, dynamic_revenue: !!form.dynamic_revenue,
          rs_dynamic_revenue: !!form.rs_dynamic_revenue, affiliate_payout: form.affiliate_payout,
          advertiser_payout: form.advertiser_payout, rs_affiliate_percent: form.rs_affiliate_percent,
          rs_advertiser_revenue: form.rs_advertiser_revenue,
          geos: form.geos, geo: form.geo, geo_rates: form.geo_rates,
        }} /> : <NeedsSave label="user access" />)}
        {tab === "Streams" && (isEdit ? <OfferStreams offerId={offerId} /> : <NeedsSave label="streams" />)}
        {tab === "Filters" && (isEdit ? <OfferFilters value={form.filters} offerId={offerId} onChange={(v) => set("filters", v)} /> : <NeedsSave label="traffic filters" />)}
        {tab === "Changes" && (isEdit ? <OfferChanges offerId={offerId} /> : <NeedsSave label="change history" />)}
      </div>

      <AlertDialog open={!!cascade} onOpenChange={(o) => { if (!o) skipCascade(); }}>
        <AlertDialogContent className="border-white/[0.1] bg-[#141417]" data-testid="offer-cascade-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Update existing streams?</AlertDialogTitle>
            <AlertDialogDescription>You changed this offer's defaults. Choose what happens to streams that still follow the old default. Streams manually set to something else are never changed.</AlertDialogDescription>
          </AlertDialogHeader>
          {cascade?.landingChanged && (
            <div className="space-y-2 rounded-lg border border-white/[0.08] bg-[#0A0A0A] p-3" data-testid="cascade-landing-block">
              <p className="text-xs font-medium text-foreground">Default landing: <span className="text-muted-foreground">{cascade.prevPrimary} → {cascade.newPrimary}</span></p>
              {[["keep", "Leave existing streams unchanged"], ["replace", `Replace landing in all streams still using "${cascade.prevPrimary}"`]].map(([v, l]) => (
                <label key={v} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input type="radio" name="cascade-landing" checked={cascade.landingChoice === v} onChange={() => setCascade((c) => ({ ...c, landingChoice: v }))} data-testid={`cascade-landing-${v}`} className="accent-white" /> {l}
                </label>
              ))}
            </div>
          )}
          {cascade?.domainChanged && (
            <div className="space-y-2 rounded-lg border border-white/[0.08] bg-[#0A0A0A] p-3" data-testid="cascade-domain-block">
              <p className="text-xs font-medium text-foreground">Default tracking domain: <span className="text-muted-foreground">{cascade.prevDomain} → {cascade.newDomain}</span></p>
              {[["keep", "Keep existing streams on the previous domain"], ["migrate", "Move all streams that inherited the old domain to the new one"]].map(([v, l]) => (
                <label key={v} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input type="radio" name="cascade-domain" checked={cascade.domainChoice === v} onChange={() => setCascade((c) => ({ ...c, domainChoice: v }))} data-testid={`cascade-domain-${v}`} className="accent-white" /> {l}
                </label>
              ))}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cascade-cancel" className="border-white/15">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={applyCascade} data-testid="cascade-confirm" className="bg-white text-black hover:bg-white/90">Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function NeedsSave({ label }) {
  return <div className="rounded-xl border border-dashed border-white/[0.1] py-12 text-center text-sm text-muted-foreground">Save the GEO offer first to manage {label}.</div>;
}

const MACRO_RE = /(\{[a-zA-Z0-9_]+\})/g;
const KNOWN_MACROS = new Set(["{subid}", "{sub_id}", "{clickid}", "{click_id}", "{orderid}", "{order_id}", "{source}", "{partner}", "{partner_id}", "{affiliate_id}", "{aff_id}", "{country}", "{geo}", "{stream}", "{stream_id}", "{offer}", "{offer_id}", ...Array.from({ length: 9 }, (_, i) => `{sub_id_${i + 1}}`), ...Array.from({ length: 9 }, (_, i) => `{sub${i + 1}}`)]);

// Renders a URL with {macros} highlighted — green if recognised, amber if unknown/typo.
function MacroUrl({ url, className }) {
  if (!url) return null;
  const parts = url.split(MACRO_RE);
  return (
    <div className={cn("break-all font-mono text-xs", className)} data-testid="landing-url-preview">
      {parts.map((p, i) => {
        if (MACRO_RE.test(p)) {
          const ok = KNOWN_MACROS.has(p.toLowerCase());
          return <span key={i} className={ok ? "text-[#22C55E]" : "text-[#EAB308]"} title={ok ? "Valid macro" : "Unknown macro — check the spelling"}>{p}</span>;
        }
        return <span key={i} className="text-muted-foreground">{p}</span>;
      })}
    </div>
  );
}

function Seg({ label, value, onChange, options, testId }) {
  return (
    <div className="space-y-1.5">
      {label && <label className="text-xs font-medium text-muted-foreground">{label}</label>}
      <div className="flex gap-1.5" data-testid={testId}>
        {options.map((o) => (
          <button key={o.value} type="button" onClick={() => onChange(o.value)} data-testid={`${testId}-${o.value}`}
            className={cn("flex-1 rounded-lg border px-3 py-2 text-xs font-medium transition-colors",
              value === o.value ? "border-[#22C55E]/50 bg-[#22C55E]/10 text-[#22C55E]" : "border-white/[0.1] text-muted-foreground hover:text-foreground")}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function BuyerAccess({ value, onChange, buyers, testId }) {
  const selected = value || [];
  const opts = (buyers || []).filter((b) => !selected.includes(b.value));
  const labelOf = (id) => (buyers || []).find((b) => b.value === id)?.label || id;
  return (
    <div className="space-y-2" data-testid={testId}>
      <label className="text-xs font-medium text-muted-foreground">Allowed buyers (Private)</label>
      <SearchSelect value="" onChange={(v) => v && onChange([...selected, v])} options={opts}
        placeholder="Search buyers by name / email / ID…" testId={`${testId}-add`} />
      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((id) => (
            <span key={id} className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.12] bg-white/[0.04] px-2.5 py-1 text-xs text-foreground" data-testid={`${testId}-chip-${id}`}>
              {labelOf(id)}
              <button type="button" onClick={() => onChange(selected.filter((x) => x !== id))} data-testid={`${testId}-remove-${id}`} className="text-muted-foreground hover:text-[#EF4444]">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      ) : <p className="text-[11px] text-muted-foreground/60">No buyers selected — a Private landing with no allowed buyers is visible to nobody.</p>}
    </div>
  );
}

// Lightbox for a saved landing's preview image — view only, closes via X / outside click / Escape.
function LandingLightbox({ landing, onClose }) {
  const img = landing ? assetUrl(landing.preview_image) : null;
  return (
    <Dialog open={!!landing} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl border-white/[0.1] bg-[#0A0A0A]" data-testid="landing-lightbox">
        <DialogTitle className="sr-only">Landing preview</DialogTitle>
        {landing && (
          <div className="space-y-3">
            <div>
              <div className="text-sm font-medium text-foreground">{landing.name || landing.url}</div>
              {landing.url && <div className="break-all text-xs text-muted-foreground">{landing.url}</div>}
            </div>
            <div className="overflow-hidden rounded-lg border border-white/[0.08]">
              {img
                ? <img src={img} alt={landing.name} className="max-h-[70vh] w-full object-contain" data-testid="landing-lightbox-img" />
                : <div className="flex h-64 items-center justify-center text-muted-foreground">No preview image uploaded for this landing.</div>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Multi-GEO picker: compact chips + CountrySelect to add. One GEO = fully legacy behaviour.
function GeoMultiPicker({ geos, onChange }) {
  return (
    <div className="col-span-2">
      <div className="mb-1.5 text-xs font-medium text-muted-foreground">GEO / Countries</div>
      <div className="flex flex-wrap items-center gap-1.5" data-testid="offer-geos">
        {(geos || []).map((g) => (
          <span key={g} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-xs text-foreground" data-testid={`offer-geo-chip-${g}`}>
            {flagEmoji(g)} {g}
            <button type="button" onClick={() => onChange((geos || []).filter((x) => x !== g))} data-testid={`offer-geo-remove-${g}`}
              className="ml-0.5 text-muted-foreground transition-colors hover:text-[#EF4444]">×</button>
          </span>
        ))}
        <div className="w-40">
          <CountrySelect value="" onChange={(v) => { const g = String(v || "").toUpperCase(); if (g && !(geos || []).includes(g)) onChange([...(geos || []), g]); }}
            placeholder="+ Add GEO" testId="offer-geo" triggerClass="h-8" />
        </div>
      </div>
      {(geos || []).length > 1 && <p className="mt-1 text-[11px] text-muted-foreground">Multi-GEO offer — set per-country rates below; countries without their own rate use the Default rate.</p>}
    </div>
  );
}

// Per-GEO rates (shown only for Multi-GEO offers). Default stays in the base fields above.
// Dynamic Revenue locks EVERY manual revenue input (default + per-GEO) without clearing values.
function GeoRatesEditor({ geos, rates, onChange, isRevShare, dynamicRevenue, currency }) {
  const upd = (g, field, v) => {
    const entry = { ...(rates?.[g] || {}) };
    if (v === "" || v == null) delete entry[field]; else entry[field] = v;
    const next = { ...(rates || {}) };
    if (Object.keys(entry).length) next[g] = entry; else delete next[g];
    onChange(next);
  };
  const num = (g, f) => { const v = rates?.[g]?.[f]; return v == null ? "" : v; };
  return (
    <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid="offer-geo-rates">
      <p className="text-sm font-medium text-foreground">GEO rates</p>
      <p className="mt-0.5 text-xs text-muted-foreground">Empty = inherits the Default rate above. Removing a GEO keeps its saved rates (hidden until the GEO is re-added).</p>
      <div className="mt-2 space-y-1.5">
        {geos.map((g) => (
          <div key={g} className="flex flex-wrap items-center gap-2" data-testid={`geo-rate-row-${g}`}>
            <span className="w-16 shrink-0 text-sm text-foreground">{flagEmoji(g)} {g}</span>
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-muted-foreground">{isRevShare ? "Revenue" : "Revenue"}</span>
              <input type="number" value={num(g, "advertiser_payout")} disabled={!!dynamicRevenue}
                onChange={(e) => upd(g, "advertiser_payout", e.target.value)} placeholder="Default"
                data-testid={`geo-rate-rev-${g}`} title={dynamicRevenue ? "Locked — revenue comes from the incoming postback" : undefined}
                className="h-8 w-24 rounded-md border border-white/[0.12] bg-[#0A0A0A] px-2 text-right font-mono text-xs text-foreground outline-none focus:border-white/30 disabled:opacity-40" />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-muted-foreground">{isRevShare ? "Payout %" : "Payout"}</span>
              <input type="number" value={num(g, "affiliate_payout")}
                onChange={(e) => upd(g, "affiliate_payout", e.target.value)} placeholder="Default"
                data-testid={`geo-rate-pay-${g}`}
                className="h-8 w-24 rounded-md border border-white/[0.12] bg-[#0A0A0A] px-2 text-right font-mono text-xs text-foreground outline-none focus:border-white/30" />
            </div>
            {!isRevShare && num(g, "advertiser_payout") !== "" && num(g, "affiliate_payout") !== "" && !dynamicRevenue && (
              <span className="text-[11px] text-muted-foreground">profit {fmtMoney((Number(num(g, "advertiser_payout")) || 0) - (Number(num(g, "affiliate_payout")) || 0), currency)}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const REDIRECT_OPTS = [{ value: "http", label: "HTTP redirect" }, { value: "no_referrer", label: "No-referrer" }];
const VIS_OPTS = [{ value: "public", label: "Public" }, { value: "private", label: "Private" }];
const emptyDraft = { url: "", name: "", preview_image: "", redirect_type: "http", visibility: "public", allowed_buyers: [], expires_at: "" };

const LANDING_PERMANENT_MSG = "This offer must always have at least one permanent landing. Create another permanent landing before setting an expiration date.";
const URL_SPACES_MSG = "URL must not contain spaces. Remove spaces and try again.";
const hasWhitespace = (s) => /\s/.test(s || "");
const toExpiryIso = (d) => (d ? new Date(`${d}T23:59:59Z`).toISOString() : "");
const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : "");
const isExpired = (iso) => !!iso && new Date(iso) < new Date();

function LandingsEditor({ items, onAdd, onRemove, onPrimary, onUpdate, buyers }) {
  const [draft, setDraft] = useState(emptyDraft);
  const [showAdd, setShowAdd] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState(emptyDraft);
  const [draftUrlError, setDraftUrlError] = useState("");
  const [editUrlError, setEditUrlError] = useState("");
  const startEdit = (l) => { setShowAdd(false); setEditId(l.id); setEditUrlError(""); setEdit({ name: l.name || "", url: l.url || "", preview_image: l.preview_image || "", redirect_type: l.redirect_type || "http", visibility: l.visibility || "public", allowed_buyers: l.allowed_buyers || [], expires_at: toDateInput(l.expires_at) }); };
  const saveEdit = (id) => {
    if (!edit.url.trim()) return toast.error("Landing URL is required");
    if (hasWhitespace(edit.url)) { setEditUrlError(URL_SPACES_MSG); return toast.error(URL_SPACES_MSG); }
    const expiryIso = toExpiryIso(edit.expires_at);
    if (expiryIso) {
      const permanentLeft = (items || []).filter((l) => (l.id === id ? false : !l.expires_at));
      if (permanentLeft.length === 0) return toast.error(LANDING_PERMANENT_MSG);
    }
    let name = (edit.name || "").trim();
    if (!name) { try { name = new URL(edit.url).hostname.replace(/^www\./, ""); } catch { name = edit.url; } }
    onUpdate(id, { name, url: edit.url.trim(), preview_image: edit.preview_image, redirect_type: edit.redirect_type, visibility: edit.visibility, allowed_buyers: edit.visibility === "private" ? edit.allowed_buyers : [], expires_at: expiryIso });
    setEditId(null);
    toast.success("Landing updated — remember to Save the offer");
  };
  const submit = () => {
    if (!draft.url.trim()) return toast.error("Enter a landing URL");
    if (hasWhitespace(draft.url)) { setDraftUrlError(URL_SPACES_MSG); return toast.error(URL_SPACES_MSG); }
    const expiryIso = toExpiryIso(draft.expires_at);
    if (expiryIso) {
      const permanentExisting = (items || []).filter((l) => !l.expires_at);
      if (permanentExisting.length === 0) return toast.error(LANDING_PERMANENT_MSG);
    }
    let name = (draft.name || "").trim();
    if (!name) { try { name = new URL(draft.url).hostname.replace(/^www\./, ""); } catch { name = draft.url; } }
    onAdd({ ...draft, name, allowed_buyers: draft.visibility === "private" ? draft.allowed_buyers : [], status: "active", expires_at: expiryIso, id: uid() });
    setDraft(emptyDraft); setDraftUrlError(""); setShowAdd(false);
  };
  return (
    <div className="space-y-4" data-testid="offer-landings">
      <SectionTitle center>Landing pages</SectionTitle>
      <div className="space-y-2.5">
        {(items || []).map((it) => {
          const inactive = it.status === "inactive";
          const img = assetUrl(it.preview_image);
          const isPrivate = (it.visibility || "public") === "private";
          if (editId === it.id) {
            return (
              <div key={it.id} className="space-y-3 rounded-xl border border-white/20 bg-white/[0.03] p-4" data-testid={`landing-edit-${it.id}`}>
                <div className="flex gap-3">
                  <div className="flex-1 space-y-2">
                    <TextField label="Landing URL" value={edit.url} error={editUrlError} onChange={(v) => { setEdit((d) => ({ ...d, url: v })); setEditUrlError(hasWhitespace(v) ? URL_SPACES_MSG : ""); }} testId={`landing-edit-url-${it.id}`} />
                    {edit.url && <div className="rounded-lg border border-white/[0.08] bg-[#0A0A0A] px-3 py-2"><MacroUrl url={edit.url} /></div>}
                    <TextField label="Name" value={edit.name} onChange={(v) => setEdit((d) => ({ ...d, name: v }))} testId={`landing-edit-name-${it.id}`} />
                  </div>
                  <AssetUpload label="Preview image" hint="Shown in the stream picker" kind="cover" value={edit.preview_image} onChange={(id) => setEdit((d) => ({ ...d, preview_image: id }))} testId={`landing-edit-preview-${it.id}`} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Seg label="Redirect type" value={edit.redirect_type} onChange={(v) => setEdit((d) => ({ ...d, redirect_type: v }))} options={REDIRECT_OPTS} testId={`landing-edit-redirect-${it.id}`} />
                  <Seg label="Visibility" value={edit.visibility} onChange={(v) => setEdit((d) => ({ ...d, visibility: v }))} options={VIS_OPTS} testId={`landing-edit-visibility-${it.id}`} />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs text-muted-foreground">Expires at (optional — leave empty for a permanent landing)</label>
                  <div className="flex items-center gap-2">
                    <Input type="date" value={edit.expires_at} onChange={(e) => setEdit((d) => ({ ...d, expires_at: e.target.value }))} data-testid={`landing-edit-expires-${it.id}`} className="max-w-[200px] border-white/[0.12] bg-[#0A0A0A]" />
                    {edit.expires_at && <button type="button" onClick={() => setEdit((d) => ({ ...d, expires_at: "" }))} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>}
                  </div>
                </div>
                {edit.visibility === "private" && <BuyerAccess value={edit.allowed_buyers} onChange={(v) => setEdit((d) => ({ ...d, allowed_buyers: v }))} buyers={buyers} testId={`landing-edit-buyers-${it.id}`} />}
                <div className="flex gap-2">
                  <Button type="button" onClick={() => saveEdit(it.id)} data-testid={`landing-save-${it.id}`} className="gap-1.5 bg-white text-black hover:bg-white/90"><Check className="h-4 w-4" /> Save</Button>
                  <Button type="button" variant="outline" onClick={() => setEditId(null)} className="border-white/[0.12]">Cancel</Button>
                </div>
              </div>
            );
          }
          return (
            <div key={it.id} className={cn("flex items-center gap-3 rounded-xl border border-white/[0.08] bg-[#0A0A0A] p-3", inactive && "opacity-60")} data-testid={`landing-row-${it.id}`}>
              <button type="button" onClick={() => onPrimary(it.id)} title="Set default" className={cn("shrink-0 transition-colors", it.primary ? "text-[#EAB308]" : "text-muted-foreground hover:text-foreground")}>
                <Star className="h-4 w-4" fill={it.primary ? "#EAB308" : "none"} />
              </button>
              <div className="h-20 w-32 shrink-0 overflow-hidden rounded-lg border border-white/[0.08] bg-[#141417]">
                {img
                  ? <button type="button" onClick={() => setLightbox(it)} title="Click to enlarge" data-testid={`landing-preview-open-${it.id}`} className="block h-full w-full cursor-zoom-in"><img src={img} alt={it.name} className="h-full w-full object-cover transition-transform hover:scale-[1.03]" /></button>
                  : <div className="flex h-full w-full items-center justify-center text-[10px] text-muted-foreground">No preview</div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 truncate text-sm text-foreground">
                  {it.name}
                  {it.primary && <span className="text-[10px] text-[#EAB308]">DEFAULT</span>}
                  <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium", isPrivate ? "bg-[#3B82F6]/15 text-[#3B82F6]" : "bg-white/[0.06] text-muted-foreground")}>{isPrivate ? "Private" : "Public"}</span>
                  {(it.redirect_type || "http") === "no_referrer" && <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] text-muted-foreground">No-ref</span>}
                </div>
                <MacroUrl url={it.url} className="mt-0.5 truncate" />
                {it.expires_at && (
                  <div className={cn("mt-1 flex items-center gap-1 text-[11px]", isExpired(it.expires_at) ? "text-[#EF4444]" : "text-muted-foreground")} data-testid={`landing-expiry-${it.id}`}>
                    <CalendarClock className="h-3 w-3" />
                    {isExpired(it.expires_at) ? "Expired — inactive" : `Expires ${fmtDate(it.expires_at)}`}
                  </div>
                )}
              </div>
              <button type="button" onClick={() => startEdit(it)} data-testid={`landing-editbtn-${it.id}`} className="shrink-0 rounded-md border border-white/[0.12] px-2 py-1 text-xs text-muted-foreground hover:text-foreground">Edit</button>
              <button type="button" onClick={() => onUpdate(it.id, { status: inactive ? "active" : "inactive" })} data-testid={`landing-toggle-${it.id}`}
                className={cn("shrink-0 rounded-md border px-2 py-1 text-xs", inactive ? "border-white/[0.12] text-muted-foreground hover:text-foreground" : "border-[#22C55E]/30 text-[#22C55E]")}>
                {inactive ? "Inactive" : "Active"}
              </button>
              <button type="button" onClick={() => onRemove(it.id)} className="shrink-0 text-muted-foreground hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
            </div>
          );
        })}
        {(items || []).length === 0 && <p className="text-center text-sm text-muted-foreground">No landings yet.</p>}
      </div>
      {!showAdd ? (
        <div className="flex justify-center pt-1">
          <Button type="button" variant="outline" onClick={() => { setEditId(null); setDraft(emptyDraft); setDraftUrlError(""); setShowAdd(true); }}
            data-testid="landing-show-add-btn" className="gap-2 border-white/[0.12] px-6"><Plus className="h-4 w-4" /> Add landing</Button>
        </div>
      ) : (
      <div className="space-y-3 rounded-xl border border-white/[0.08] bg-white/[0.02] p-4" data-testid="landing-add-form">
        <div className="flex gap-3">
          <div className="flex-1 space-y-2">
            <TextField label="Landing URL" value={draft.url} error={draftUrlError} onChange={(v) => { setDraft((d) => ({ ...d, url: v })); setDraftUrlError(hasWhitespace(v) ? URL_SPACES_MSG : ""); }} testId="landing-url-input" placeholder="https://lp.io/offer?sub1={subid}&sub2={source}" />
            {draft.url && (
              <div className="rounded-lg border border-white/[0.08] bg-[#0A0A0A] px-3 py-2">
                <div className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">Live preview</div>
                <MacroUrl url={draft.url} />
              </div>
            )}
            <TextField label="Name (optional)" value={draft.name} onChange={(v) => setDraft((d) => ({ ...d, name: v }))} testId="landing-name-input" placeholder="Auto from domain if empty" />
          </div>
          <AssetUpload label="Preview image" hint="Shown in the stream picker" kind="cover" value={draft.preview_image} onChange={(id) => setDraft((d) => ({ ...d, preview_image: id }))} testId="landing-preview-upload" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Seg label="Redirect type" value={draft.redirect_type} onChange={(v) => setDraft((d) => ({ ...d, redirect_type: v }))} options={REDIRECT_OPTS} testId="landing-redirect" />
          <Seg label="Visibility" value={draft.visibility} onChange={(v) => setDraft((d) => ({ ...d, visibility: v }))} options={VIS_OPTS} testId="landing-visibility" />
        </div>
        {draft.visibility === "private" && <BuyerAccess value={draft.allowed_buyers} onChange={(v) => setDraft((d) => ({ ...d, allowed_buyers: v }))} buyers={buyers} testId="landing-buyers" />}
        <div>
          <label className="mb-1.5 block text-xs text-muted-foreground">Expires at (optional — leave empty for a permanent landing)</label>
          <div className="flex items-center gap-2">
            <Input type="date" value={draft.expires_at} onChange={(e) => setDraft((d) => ({ ...d, expires_at: e.target.value }))} data-testid="landing-expires-input" className="max-w-[200px] border-white/[0.12] bg-[#0A0A0A]" />
            {draft.expires_at && <button type="button" onClick={() => setDraft((d) => ({ ...d, expires_at: "" }))} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button type="button" onClick={submit} data-testid="landing-add-btn" className="gap-1.5 bg-white text-black hover:bg-white/90"><Plus className="h-4 w-4" /> Add landing</Button>
          <Button type="button" variant="outline" onClick={() => { setShowAdd(false); setDraft(emptyDraft); setDraftUrlError(""); }} data-testid="landing-add-cancel" className="border-white/[0.12]">Cancel</Button>
        </div>
      </div>
      )}
      <LandingLightbox landing={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}

const emptyGeoDraft = { url: "", name: "", preview_image: "", redirect_type: "http", expires_at: "", geo: "" };

// "Assign landing to" row: Country | 🌐 Fallback segmented buttons + inline searchable country
// picker. The Fallback option is disabled while a Fallback already exists (delete it to replace);
// `locked` pins the existing Fallback landing to its role while editing.
function AssignRow({ assign, onAssign, geo, onGeo, fallbackTaken, locked, testId }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-muted-foreground">Assign landing to</label>
      <div className="flex flex-wrap items-center gap-1.5" data-testid={testId}>
        {[{ value: "geo", label: "Country" }, { value: "fallback", label: "🌐 Fallback — all other GEOs" }].map((o) => {
          const disabled = locked || (o.value === "fallback" && fallbackTaken && assign !== "fallback");
          return (
            <button key={o.value} type="button" disabled={disabled} onClick={() => !disabled && onAssign(o.value)} data-testid={`${testId}-${o.value}`}
              title={!locked && o.value === "fallback" && fallbackTaken && assign !== "fallback" ? "A Fallback landing already exists — delete it first to assign a new one" : undefined}
              className={cn("rounded-lg border px-3 py-2 text-xs font-medium transition-colors",
                assign === o.value ? "border-[#22C55E]/50 bg-[#22C55E]/10 text-[#22C55E]" : "border-white/[0.1] text-muted-foreground",
                disabled ? "cursor-not-allowed opacity-50" : assign !== o.value && "hover:text-foreground")}>
              {o.label}
            </button>
          );
        })}
        {assign === "geo"
          ? <div className="w-60"><CountrySelect value={geo} onChange={onGeo} worldwide={false} testId={`${testId}-country`} triggerClass="h-9" /></div>
          : <span className="pl-1 text-xs text-muted-foreground/70">Serves every country without its own landing.</span>}
      </div>
    </div>
  );
}

function GeoLandingsEditor({ items, onAdd, onRemove, onUpdate }) {
  const list = items || [];
  const [draft, setDraft] = useState(emptyGeoDraft);
  const [assign, setAssign] = useState("geo");
  const [showAdd, setShowAdd] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState(emptyGeoDraft);
  const [editAssign, setEditAssign] = useState("geo");
  const [draftUrlError, setDraftUrlError] = useState("");
  const [editUrlError, setEditUrlError] = useState("");
  const activeGeoTaken = (geo, exceptId) => list.some((l) => l.id !== exceptId && !l.fallback && l.status !== "inactive" && (l.geo || "").toUpperCase() === (geo || "").toUpperCase());
  const activeFallback = list.find((l) => l.fallback && l.status !== "inactive");
  const autoName = (name, url) => { let n = (name || "").trim(); if (!n) { try { n = new URL(url).hostname.replace(/^www\./, ""); } catch { n = url; } } return n; };

  const submit = () => {
    if (!draft.url.trim()) return toast.error("Enter a landing URL");
    if (hasWhitespace(draft.url)) { setDraftUrlError(URL_SPACES_MSG); return toast.error(URL_SPACES_MSG); }
    const isFb = assign === "fallback";
    if (!isFb && !draft.geo) return toast.error("Select a GEO for this landing");
    if (!isFb && activeGeoTaken(draft.geo)) return toast.error(`${geoName(draft.geo)} already has an active landing — only one active landing per GEO`);
    if (isFb && activeFallback) return toast.error("A Fallback landing already exists — delete it first to assign a new one");
    onAdd({ ...draft, url: draft.url.trim(), name: autoName(draft.name, draft.url), geo: isFb ? "" : draft.geo, fallback: isFb,
      expires_at: isFb ? "" : toExpiryIso(draft.expires_at), status: "active", id: uid() });
    setDraft(emptyGeoDraft); setAssign("geo"); setDraftUrlError(""); setShowAdd(false);
  };

  const startEdit = (l) => {
    setShowAdd(false);
    setEditId(l.id); setEditUrlError(""); setEditAssign(l.fallback ? "fallback" : "geo");
    setEdit({ url: l.url || "", name: l.name || "", preview_image: l.preview_image || "", redirect_type: l.redirect_type || "http",
      expires_at: toDateInput(l.expires_at), geo: l.geo || "" });
  };

  const saveEdit = (id) => {
    if (!edit.url.trim()) return toast.error("Landing URL is required");
    if (hasWhitespace(edit.url)) { setEditUrlError(URL_SPACES_MSG); return toast.error(URL_SPACES_MSG); }
    const row = list.find((l) => l.id === id);
    const isFb = row?.fallback ? true : editAssign === "fallback";
    if (!isFb && !edit.geo) return toast.error("Select a GEO for this landing");
    if (!isFb && row?.status !== "inactive" && activeGeoTaken(edit.geo, id)) return toast.error(`${geoName(edit.geo)} already has an active landing`);
    if (isFb && activeFallback && activeFallback.id !== id) return toast.error("A Fallback landing already exists — delete it first to assign a new one");
    onUpdate(id, { url: edit.url.trim(), name: autoName(edit.name, edit.url), preview_image: edit.preview_image, redirect_type: edit.redirect_type,
      expires_at: isFb ? "" : toExpiryIso(edit.expires_at), geo: isFb ? "" : edit.geo, fallback: isFb });
    setEditId(null);
    toast.success("Landing updated — remember to Save the offer");
  };

  const toggleStatus = (l) => {
    const inactive = l.status === "inactive";
    if (inactive && !l.fallback && activeGeoTaken(l.geo, l.id)) return toast.error(`${geoName(l.geo)} already has an active landing`);
    if (inactive && l.fallback && activeFallback && activeFallback.id !== l.id) return toast.error("A Fallback landing already exists");
    onUpdate(l.id, { status: inactive ? "active" : "inactive" });
  };

  const rows = [...list.filter((l) => !l.fallback), ...list.filter((l) => l.fallback)];
  return (
    <div className="space-y-4" data-testid="offer-geo-landings">
      <SectionTitle center>Multi-GEO landing pages</SectionTitle>
      <div className="space-y-2.5">
        {rows.map((it) => {
          const inactive = it.status === "inactive";
          const img = assetUrl(it.preview_image);
          if (editId === it.id) {
            return (
              <div key={it.id} className="space-y-3 rounded-xl border border-white/20 bg-white/[0.03] p-4" data-testid={`geo-landing-edit-${it.id}`}>
                <div className="flex gap-3">
                  <div className="flex-1 space-y-2">
                    <TextField label="Landing URL" value={edit.url} error={editUrlError} onChange={(v) => { setEdit((d) => ({ ...d, url: v })); setEditUrlError(hasWhitespace(v) ? URL_SPACES_MSG : ""); }} testId={`geo-landing-edit-url-${it.id}`} />
                    {edit.url && <div className="rounded-lg border border-white/[0.08] bg-[#0A0A0A] px-3 py-2"><MacroUrl url={edit.url} /></div>}
                    <TextField label="Name" value={edit.name} onChange={(v) => setEdit((d) => ({ ...d, name: v }))} testId={`geo-landing-edit-name-${it.id}`} />
                  </div>
                  <AssetUpload label="Preview image" hint="Shown in routing previews" kind="cover" value={edit.preview_image} onChange={(id) => setEdit((d) => ({ ...d, preview_image: id }))} testId={`geo-landing-edit-preview-${it.id}`} />
                </div>
                <AssignRow assign={it.fallback ? "fallback" : editAssign} onAssign={setEditAssign} geo={edit.geo}
                  onGeo={(v) => setEdit((d) => ({ ...d, geo: v }))} fallbackTaken={!!activeFallback} locked={!!it.fallback} testId={`geo-landing-edit-assign-${it.id}`} />
                <div className="max-w-xs">
                  <Seg label="Redirect type" value={edit.redirect_type} onChange={(v) => setEdit((d) => ({ ...d, redirect_type: v }))} options={REDIRECT_OPTS} testId={`geo-landing-edit-redirect-${it.id}`} />
                </div>
                {!it.fallback && editAssign === "geo" && (
                  <div>
                    <label className="mb-1.5 block text-xs text-muted-foreground">Expires at (optional)</label>
                    <div className="flex items-center gap-2">
                      <Input type="date" value={edit.expires_at} onChange={(e) => setEdit((d) => ({ ...d, expires_at: e.target.value }))} data-testid={`geo-landing-edit-expires-${it.id}`} className="max-w-[200px] border-white/[0.12] bg-[#0A0A0A]" />
                      {edit.expires_at && <button type="button" onClick={() => setEdit((d) => ({ ...d, expires_at: "" }))} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>}
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button type="button" onClick={() => saveEdit(it.id)} data-testid={`geo-landing-save-${it.id}`} className="gap-1.5 bg-white text-black hover:bg-white/90"><Check className="h-4 w-4" /> Save</Button>
                  <Button type="button" variant="outline" onClick={() => setEditId(null)} className="border-white/[0.12]">Cancel</Button>
                </div>
              </div>
            );
          }
          return (
            <div key={it.id} className={cn("flex items-center gap-3 rounded-xl border p-3", it.fallback ? "border-[#3B82F6]/25 bg-[#3B82F6]/[0.04]" : "border-white/[0.08] bg-[#0A0A0A]", inactive && "opacity-60")} data-testid={`geo-landing-row-${it.id}`}>
              <div className="h-20 w-32 shrink-0 overflow-hidden rounded-lg border border-white/[0.08] bg-[#141417]">
                {img
                  ? <button type="button" onClick={() => setLightbox(it)} title="Click to enlarge" data-testid={`geo-landing-preview-open-${it.id}`} className="block h-full w-full cursor-zoom-in"><img src={img} alt={it.name} className="h-full w-full object-cover transition-transform hover:scale-[1.03]" /></button>
                  : <div className="flex h-full w-full items-center justify-center text-[10px] text-muted-foreground">No preview</div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 truncate text-sm text-foreground">
                  {it.name}
                  <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium", it.fallback ? "bg-[#3B82F6]/15 text-[#3B82F6]" : "bg-white/[0.06] text-foreground")} data-testid={`geo-landing-geo-${it.id}`}>
                    {it.fallback ? "🌐 All other GEOs · FALLBACK" : `${flagEmoji(it.geo)} ${geoName(it.geo)}`}
                  </span>
                  {(it.redirect_type || "http") === "no_referrer" && <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] text-muted-foreground">No-ref</span>}
                </div>
                <MacroUrl url={it.url} className="mt-0.5 truncate" />
                {it.expires_at && (
                  <div className={cn("mt-1 flex items-center gap-1 text-[11px]", isExpired(it.expires_at) ? "text-[#EF4444]" : "text-muted-foreground")} data-testid={`geo-landing-expiry-${it.id}`}>
                    <CalendarClock className="h-3 w-3" />
                    {isExpired(it.expires_at) ? "Expired — inactive" : `Expires ${fmtDate(it.expires_at)}`}
                  </div>
                )}
              </div>
              <button type="button" onClick={() => startEdit(it)} data-testid={`geo-landing-editbtn-${it.id}`} className="shrink-0 rounded-md border border-white/[0.12] px-2 py-1 text-xs text-muted-foreground hover:text-foreground">Edit</button>
              <button type="button" onClick={() => toggleStatus(it)} data-testid={`geo-landing-toggle-${it.id}`}
                className={cn("shrink-0 rounded-md border px-2 py-1 text-xs", inactive ? "border-white/[0.12] text-muted-foreground hover:text-foreground" : "border-[#22C55E]/30 text-[#22C55E]")}>
                {inactive ? "Inactive" : "Active"}
              </button>
              <button type="button" onClick={() => onRemove(it.id)} data-testid={`geo-landing-remove-${it.id}`} className="shrink-0 text-muted-foreground hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
            </div>
          );
        })}
        {rows.length === 0 && <p className="text-center text-sm text-muted-foreground">No Multi-GEO landings yet — add one per country and mark one as 🌐 Fallback.</p>}
      </div>
      {!showAdd ? (
        <div className="flex justify-center pt-1">
          <Button type="button" variant="outline" onClick={() => { setEditId(null); setDraft(emptyGeoDraft); setAssign("geo"); setDraftUrlError(""); setShowAdd(true); }}
            data-testid="geo-landing-show-add-btn" className="gap-2 border-white/[0.12] px-6"><Plus className="h-4 w-4" /> Add landing</Button>
        </div>
      ) : (
      <div className="space-y-3 rounded-xl border border-white/[0.08] bg-white/[0.02] p-4" data-testid="geo-landing-add-form">
        <div className="flex gap-3">
          <div className="flex-1 space-y-2">
            <TextField label="Landing URL" value={draft.url} error={draftUrlError} onChange={(v) => { setDraft((d) => ({ ...d, url: v })); setDraftUrlError(hasWhitespace(v) ? URL_SPACES_MSG : ""); }} testId="geo-landing-url-input" placeholder="https://lp.io/offer?sub1={subid}&sub2={source}" />
            {draft.url && (
              <div className="rounded-lg border border-white/[0.08] bg-[#0A0A0A] px-3 py-2">
                <div className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">Live preview</div>
                <MacroUrl url={draft.url} />
              </div>
            )}
            <TextField label="Name (optional)" value={draft.name} onChange={(v) => setDraft((d) => ({ ...d, name: v }))} testId="geo-landing-name-input" placeholder="Auto from domain if empty" />
          </div>
          <AssetUpload label="Preview image" hint="Shown in routing previews" kind="cover" value={draft.preview_image} onChange={(id) => setDraft((d) => ({ ...d, preview_image: id }))} testId="geo-landing-preview-upload" />
        </div>
        <AssignRow assign={assign} onAssign={setAssign} geo={draft.geo} onGeo={(v) => setDraft((d) => ({ ...d, geo: v }))}
          fallbackTaken={!!activeFallback} testId="geo-landing-assign" />
        <div className="max-w-xs">
          <Seg label="Redirect type" value={draft.redirect_type} onChange={(v) => setDraft((d) => ({ ...d, redirect_type: v }))} options={REDIRECT_OPTS} testId="geo-landing-redirect" />
        </div>
        {assign === "geo" && (
          <div>
            <label className="mb-1.5 block text-xs text-muted-foreground">Expires at (optional — leave empty for a permanent landing)</label>
            <div className="flex items-center gap-2">
              <Input type="date" value={draft.expires_at} onChange={(e) => setDraft((d) => ({ ...d, expires_at: e.target.value }))} data-testid="geo-landing-expires-input" className="max-w-[200px] border-white/[0.12] bg-[#0A0A0A]" />
              {draft.expires_at && <button type="button" onClick={() => setDraft((d) => ({ ...d, expires_at: "" }))} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>}
            </div>
          </div>
        )}
        <div className="flex gap-2">
          <Button type="button" onClick={submit} data-testid="geo-landing-add-btn" className="gap-1.5 bg-white text-black hover:bg-white/90"><Plus className="h-4 w-4" /> Add landing</Button>
          <Button type="button" variant="outline" onClick={() => { setShowAdd(false); setDraft(emptyGeoDraft); setAssign("geo"); setDraftUrlError(""); }} data-testid="geo-landing-add-cancel" className="border-white/[0.12]">Cancel</Button>
        </div>
      </div>
      )}
      <LandingLightbox landing={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}

function ArrayEditor({ title, items, fields, selects = [], onAdd, onRemove, onPrimary, testId }) {
  const [draft, setDraft] = useState({});
  const submit = () => {
    const required = fields.filter((f) => !f.optional);
    if (required.some((f) => !draft[f.key])) {
      toast.error(`Please fill in: ${required.map((f) => f.placeholder).join(", ")}`);
      return;
    }
    if (fields.every((f) => !draft[f.key])) return;
    const item = { ...draft };
    selects.forEach((s) => { if (!item[s.key]) item[s.key] = s.default; });
    onAdd(item);
    setDraft({});
  };
  return (
    <div className="max-w-3xl space-y-4" data-testid={`offer-${testId}`}>
      <SectionTitle>{title}</SectionTitle>
      <div className="space-y-2">
        {(items || []).map((it) => (
          <div key={it.id} className="flex items-center gap-3 rounded-lg border border-white/[0.08] bg-[#0A0A0A] px-3 py-2.5">
            {onPrimary && (
              <button onClick={() => onPrimary(it.id)} title="Set primary" className={cn("transition-colors", it.primary ? "text-[#EAB308]" : "text-muted-foreground hover:text-foreground")}>
                <Star className="h-4 w-4" fill={it.primary ? "#EAB308" : "none"} />
              </button>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-foreground">{it.name || it.domain}</div>
              {it.url && <div className="truncate text-xs text-muted-foreground">{it.url}</div>}
            </div>
            {it.type && <span className="rounded bg-white/[0.05] px-2 py-0.5 text-xs capitalize text-muted-foreground">{it.type}</span>}
            <StatusBadge status={it.status} />
            <button onClick={() => onRemove(it.id)} className="text-muted-foreground hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {(items || []).length === 0 && <p className="text-sm text-muted-foreground">Nothing added yet.</p>}
      </div>
      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-white/[0.08] bg-white/[0.02] p-3">
        {fields.map((f) => (
          <Input key={f.key} placeholder={f.placeholder} value={draft[f.key] || ""}
            onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
            className="h-9 flex-1 border-white/[0.1] bg-[#0A0A0A] text-sm" />
        ))}
        <Button onClick={submit} className="h-9 gap-1.5 bg-white text-black hover:bg-white/90" data-testid={`add-${testId}-btn`}>
          <Plus className="h-4 w-4" /> Add
        </Button>
      </div>
    </div>
  );
}

function OfferUsers({ offerId, draft }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["offer-related-users", offerId],
    queryFn: async () => (await http.get(`/offers/${offerId}/related-users`)).data,
  });
  // Draft (unsaved editor state) wins over the persisted defaults so the override matrix
  // reacts instantly when the manager switches the Payment model / revenue toggles.
  const defaults = { ...(data?.offer_defaults || {}), ...(draft || {}) };
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [geoDlg, setGeoDlg] = useState(null);
  const offerGeos = Array.isArray(defaults.geos) && defaults.geos.length
    ? defaults.geos
    : String(defaults.geo || "").split(",").map((x) => x.trim().toUpperCase()).filter(Boolean);
  const multiGeo = offerGeos.length > 1;

  useEffect(() => { if (data?.items) setRows(data.items.map((r) => ({ ...r }))); }, [data]);

  // Unified override matrix: only the fields that make sense for the active payment model
  // (and its revenue source) are editable — same rules as User → Offer Rates.
  const model = defaults.payment_model === "RevShare" ? "RevShare" : defaults.payment_model === "Hybrid" ? "Hybrid" : "CPA";
  const cpaActive = model !== "RevShare";
  const rsActive = model === "RevShare" || model === "Hybrid";
  const cpaDynamic = cpaActive && !!defaults.dynamic_revenue;
  const rsDynamic = rsActive && (model === "RevShare" ? !!defaults.dynamic_revenue : !!defaults.rs_dynamic_revenue);
  const rsPctDefault = model === "RevShare" ? defaults.affiliate_payout : defaults.rs_affiliate_percent;
  const rsRevDefault = model === "RevShare" ? defaults.advertiser_payout : defaults.rs_advertiser_revenue;

  const fmtNum = (v) => (v === null || v === undefined || v === "" ? "" : v);
  const setCell = (uid, key, val) =>
    setRows((rs) => rs.map((r) => (r.user_id === uid ? { ...r, [key]: val } : r)));

  const filtered = rows.filter((r) => (r.streams_count || 0) > 0).filter((r) => {
    if (!q.trim()) return true;
    const s = `${r.user_seq || ""} ${r.name || ""} ${r.email || ""}`.toLowerCase();
    return s.includes(q.trim().toLowerCase());
  });

  const save = async () => {
    setSaving(true);
    try {
      await http.put(`/offers/${offerId}/related-users`, {
        items: rows.map((r) => {
          const it = { user_id: r.user_id, hold: r.hold, cap: r.cap, status: r.status || "active" };
          if (multiGeo) it.geo_overrides = r.geo_overrides || {};
          if (cpaActive) {
            it.cpa_payout_override = r.cpa_payout_override;
            if (!cpaDynamic) it.cpa_revenue_override = r.cpa_revenue_override;
          }
          if (rsActive) {
            it.rs_payout_override = r.rs_payout_override;
            it.rs_adv_percent_override = r.rs_adv_percent_override;
          }
          return it;
        }),
      });
      qc.invalidateQueries({ queryKey: ["offer-related-users", offerId] });
      toast.success("Overrides saved", { description: `${filtered.length} user(s) updated` });
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || "Try again." });
    } finally { setSaving(false); }
  };

  const numCell = (uid, field) => (
    <input type="number" inputMode="decimal" value={fmtNum(rows.find((r) => r.user_id === uid)?.[field])}
      onChange={(e) => setCell(uid, field, e.target.value)} placeholder="Inherited"
      data-testid={`ovr-${field}-${uid}`}
      className="h-8 w-20 rounded-md border border-transparent bg-transparent px-2 text-right text-sm text-zinc-900 dark:text-zinc-100 outline-none transition-all placeholder:text-zinc-400 dark:placeholder:text-zinc-600 hover:border-zinc-300 dark:hover:border-white/20 focus:border-zinc-400 dark:focus:border-white/30 focus:bg-zinc-100 dark:focus:bg-[#0E0E10]" />
  );
  const ovrCell = (r, field, active, dynamic) => {
    if (!active) return <span className="text-xs text-muted-foreground/40" data-testid={`ovr-${field}-${r.user_id}-na`}>—</span>;
    if (dynamic) return <span className="text-xs font-medium text-[#F59E0B]" data-testid={`ovr-${field}-${r.user_id}-dynamic`}>Dynamic</span>;
    return numCell(r.user_id, field);
  };

  const defParts = [];
  if (cpaActive) defParts.push(`CPA: ${fmtMoney(defaults.affiliate_payout)} payout · ${cpaDynamic ? "Dynamic" : fmtMoney(defaults.advertiser_payout)} revenue`);
  if (rsActive) defParts.push(`RevShare: ${Number(rsPctDefault) || 0}% payout · ${rsDynamic ? "Dynamic" : fmtMoney(rsRevDefault)} revenue`);

  return (
    <div className="space-y-4" data-testid="offer-users">
      {geoDlg && (
        <GeoOverridesDialog open={!!geoDlg} onClose={() => setGeoDlg(null)} geos={offerGeos}
          value={geoDlg.geo_overrides || {}}
          onApply={(m) => setCell(geoDlg.user_id, "geo_overrides", m)}
          percent={model === "RevShare"} staff
          dynamicRevenue={model === "RevShare" ? rsDynamic : cpaDynamic}
          geoRates={defaults.geo_rates || {}}
          defaults={{ payout: model === "RevShare" ? rsPctDefault : defaults.affiliate_payout, revenue: defaults.advertiser_payout }} />
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-medium text-white">Users</h4>
          <p className="mt-0.5 text-xs text-muted-foreground">Empty override inherits the offer default ({defParts.join(" · ")}).</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by ID, name, email…"
            data-testid="offer-users-search" className="h-9 w-[240px] rounded-lg border-white/10 bg-[#0E0E10] pl-9 text-sm" />
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-white/[0.06]">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-white/[0.06] text-left text-[11px] uppercase tracking-wider text-zinc-500">
              <th className="px-4 py-3 font-medium">ID</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-3 py-3 text-right font-medium">CPA Rev</th>
              <th className="px-3 py-3 text-right font-medium">CPA Payout</th>
              <th className="px-3 py-3 text-right font-medium">RS ADV %</th>
              <th className="px-3 py-3 text-right font-medium">RS Payout %</th>
              {multiGeo && <th className="px-3 py-3 text-right font-medium">GEO</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={multiGeo ? 8 : 7} className="px-4 py-6 text-center text-sm text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading…</td></tr>}
            {!isLoading && filtered.length === 0 && (
              <tr><td colSpan={multiGeo ? 8 : 7} className="px-4 py-10 text-center text-sm text-muted-foreground">No users yet — a user appears here automatically once they create a stream on this GEO offer.</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.user_id} data-testid={`offer-user-row-${r.user_id}`} className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.02]">
                <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{r.user_seq != null ? `#${r.user_seq}` : "—"}</td>
                <td className="px-4 py-2.5 text-foreground">{r.name}</td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">{r.email || "—"}</td>
                <td className="px-3 py-2.5 text-right">{ovrCell(r, "cpa_revenue_override", cpaActive, cpaDynamic)}</td>
                <td className="px-3 py-2.5 text-right">{ovrCell(r, "cpa_payout_override", cpaActive, false)}</td>
                <td className="px-3 py-2.5 text-right">{ovrCell(r, "rs_adv_percent_override", rsActive, false)}</td>
                <td className="px-3 py-2.5 text-right">{ovrCell(r, "rs_payout_override", rsActive, false)}</td>
                {multiGeo && (
                  <td className="px-3 py-2.5 text-right">
                    <button type="button" onClick={() => setGeoDlg(r)} data-testid={`geo-ovr-open-${r.user_id}`}
                      className={`rounded border px-1.5 py-0.5 text-xs transition-colors hover:border-white/30 ${Object.keys(r.geo_overrides || {}).length ? "border-[#22C55E]/40 text-[#22C55E]" : "border-white/[0.1] text-muted-foreground"}`}>
                      GEO{Object.keys(r.geo_overrides || {}).length ? ` · ${Object.keys(r.geo_overrides || {}).length}` : ""}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{filtered.length} user(s)</span>
        <Button onClick={save} disabled={saving || isLoading} data-testid="offer-users-save" className="h-9 gap-1.5 rounded-lg bg-white text-black hover:bg-white/90">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save overrides
        </Button>
      </div>
    </div>
  );
}

function OfferStreams({ offerId }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["offer-streams", offerId], queryFn: async () => (await http.get(`/offers/${offerId}/streams`)).data });
  const streams = data?.items || [];
  const [q, setQ] = useState("");
  const [del, setDel] = useState({ open: false, stream: null });
  const [busy, setBusy] = useState(false);

  const filtered = streams.filter((s) => {
    if (!q.trim()) return true;
    const t = `${s.buyer_seq || ""} ${s.name || ""} ${s.buyer_name || ""} ${s.buyer_email || ""} ${s.traffic_source || ""}`.toLowerCase();
    return t.includes(q.trim().toLowerCase());
  });

  const doDelete = async () => {
    setBusy(true);
    try {
      await http.delete(`/streams/${del.stream.id}`);
      ["offer-streams", "offer-related-users", "conversions", "balances", "dashboard", "statistics", "stats-report"].forEach((k) => qc.invalidateQueries({ queryKey: k === "offer-streams" || k === "offer-related-users" ? [k, offerId] : [k] }));
      toast.success("Stream deleted", { description: "All related data was removed and balances recalculated." });
      setDel({ open: false, stream: null });
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || "Try again." });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="offer-streams">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="text-sm font-medium text-white">Streams <span className="text-muted-foreground">· {streams.length}</span></h4>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by ID, name, email…"
            data-testid="offer-streams-search" className="h-9 w-[260px] rounded-lg border-white/10 bg-[#0E0E10] pl-9 text-sm" />
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-white/[0.06]">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-white/[0.06] text-left text-[11px] uppercase tracking-wider text-zinc-500">
              <th className="px-4 py-3 font-medium">Stream</th>
              <th className="px-4 py-3 font-medium">Buyer</th>
              <th className="px-4 py-3 font-medium">Source</th>
              <th className="px-4 py-3 text-right font-medium">Clicks</th>
              <th className="px-4 py-3 text-right font-medium">Conv</th>
              <th className="px-4 py-3 text-right font-medium">Payout</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3 text-right font-medium" />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-muted-foreground">No streams found.</td></tr>}
            {filtered.map((s) => (
              <tr key={s.id} data-testid={`offer-stream-row-${s.id}`} className="border-b border-white/[0.04] last:border-0 transition-colors hover:bg-white/[0.02]">
                <td className="px-4 py-2.5"><div className="text-foreground">{s.name || "—"}</div><div className="font-mono text-[11px] text-muted-foreground">#{s.tenant_seq}</div></td>
                <td className="px-4 py-2.5"><div className="text-foreground">{s.buyer_name || "—"}</div><div className="text-[11px] text-muted-foreground">{s.buyer_email || (s.buyer_seq != null ? `#${s.buyer_seq}` : "")}</div></td>
                <td className="px-4 py-2.5 text-muted-foreground">{s.traffic_source || "—"}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-foreground/80">{s.clicks ?? 0}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-foreground/80">{s.conversions ?? 0}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-foreground">{fmtMoney(s.payout)}</td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">{s.created_at ? fmtDate(s.created_at) : "—"}</td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => setDel({ open: true, stream: s })} data-testid={`offer-stream-delete-${s.id}`}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-[#EF4444]/10 hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={del.open} onOpenChange={(o) => setDel((s) => ({ ...s, open: o }))}>
        <AlertDialogContent className="border-white/[0.1] bg-[#0A0A0A]" data-testid="offer-stream-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this Stream permanently?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone. Deleting <span className="font-medium text-foreground">{del.stream?.name}</span> will also permanently delete all related data:</AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="ml-1 space-y-1 rounded-lg border border-[#EF4444]/25 bg-[#EF4444]/[0.06] p-3 text-xs text-muted-foreground">
            <li>• all clicks and conversions (leads, deposits)</li>
            <li>• all postbacks and delivery logs</li>
            <li>• all statistics and financial records</li>
            <li>• buyer balances will be recalculated automatically</li>
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="offer-stream-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} disabled={busy} data-testid="offer-stream-delete-confirm" className="bg-[#EF4444] text-white hover:bg-[#dc2626]">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function OfferChanges({ offerId }) {
  const { data } = useQuery({ queryKey: ["offer-changes", offerId], queryFn: async () => (await http.get(`/offers/${offerId}/changes`)).data });
  const changes = data?.items || [];
  return (
    <div className="space-y-3" data-testid="offer-changes">
      <SectionTitle>Change history</SectionTitle>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {changes.map((ch) => (
          <div key={ch.id} className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3.5 text-sm">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white/40" />
            <div className="min-w-0">
              <div className="break-words text-foreground/90">{ch.action}</div>
              <div className="mt-1 text-xs text-muted-foreground">{ch.actor || "System"} · {fmtDateTime(ch.created_at)}</div>
            </div>
          </div>
        ))}
      </div>
      {changes.length === 0 && <p className="text-sm text-muted-foreground">No changes recorded yet.</p>}
    </div>
  );
}
